import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Apple, Smartphone, Gift, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const INSTALL_FLAG = 'cp_post_register_seen';

export default function Welcome() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    try { localStorage.setItem(INSTALL_FLAG, '1'); } catch {}
  }, [user, loading, navigate]);

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white font-['Manrope'] px-4 py-12 flex items-center justify-center">
      <div className="fixed inset-0 -z-10 opacity-40 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 20% 0%, rgba(255,193,7,0.14), transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,107,53,0.10), transparent 50%)' }} />

      <div className="w-full max-w-3xl">
        {/* Bonus Badge */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#FFC107]/20 to-[#FF9800]/20 border border-[#FFC107]/40 text-[#FFC107] text-sm font-bold mb-4" data-testid="welcome-badge">
            <Gift className="w-4 h-4" /> 7 jours Pro offerts
          </div>
          <h1 className="text-3xl sm:text-5xl font-black mb-3">
            Bienvenue {user.name ? user.name : ''} !
          </h1>
          <p className="text-base sm:text-lg text-[#C8C8C8] max-w-xl mx-auto leading-relaxed">
            Votre compte est créé. Choisissez votre plateforme pour démarrer
            <strong className="text-white"> 7 jours d'essai Pro gratuit</strong> sur l'application ClayPulse.
          </p>
        </div>

        {/* Two big cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-testid="platform-choice">
          {/* iOS */}
          <Link
            to="/ios"
            data-testid="platform-ios"
            className="group rounded-2xl bg-gradient-to-br from-[#161616] to-[#0F0F0F] border border-white/10 hover:border-[#FFC107]/50 p-6 transition-all hover:shadow-[0_0_30px_rgba(255,193,7,0.15)]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-[#FFC107]/15 transition-colors">
                <Apple className="w-6 h-6 text-white group-hover:text-[#FFC107] transition-colors" />
              </div>
              <span className="px-2.5 py-0.5 text-[10px] uppercase tracking-widest rounded-full bg-white/5 text-[#888] font-bold">iPhone · iPad</span>
            </div>
            <h2 className="text-xl font-black mb-2">Installer la PWA</h2>
            <p className="text-sm text-[#A0A0A0] leading-relaxed mb-4">
              Application web progressive — aucun App Store, installation directe depuis Safari en 10 secondes.
            </p>
            <ul className="space-y-1.5 text-xs text-[#C8C8C8] mb-5">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-400 shrink-0" /> Fonctionne hors-ligne</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-400 shrink-0" /> Sans App Store</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-400 shrink-0" /> Toujours à jour</li>
            </ul>
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#FFC107] group-hover:gap-2.5 transition-all">
              Voir les instructions <ArrowRight className="w-4 h-4" />
            </span>
          </Link>

          {/* Android */}
          <Link
            to="/android"
            data-testid="platform-android"
            className="group rounded-2xl bg-gradient-to-br from-[#161616] to-[#0F0F0F] border border-white/10 hover:border-[#FF9800]/50 p-6 transition-all hover:shadow-[0_0_30px_rgba(255,152,0,0.15)]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-[#FF9800]/15 transition-colors">
                <Smartphone className="w-6 h-6 text-white group-hover:text-[#FF9800] transition-colors" />
              </div>
              <span className="px-2.5 py-0.5 text-[10px] uppercase tracking-widest rounded-full bg-white/5 text-[#888] font-bold">Android</span>
            </div>
            <h2 className="text-xl font-black mb-2">Télécharger l'APK</h2>
            <p className="text-sm text-[#A0A0A0] leading-relaxed mb-4">
              Application native Android — fichier APK officiel, installation directe depuis votre téléphone.
            </p>
            <ul className="space-y-1.5 text-xs text-[#C8C8C8] mb-5">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-400 shrink-0" /> Application native rapide</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-400 shrink-0" /> Notifications push</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-400 shrink-0" /> Mise à jour manuelle</li>
            </ul>
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#FF9800] group-hover:gap-2.5 transition-all">
              Télécharger l'APK <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        </div>

        {/* Skip link */}
        <div className="text-center">
          <Link to="/mon-espace" className="text-sm text-[#888] hover:text-white inline-flex items-center gap-1.5" data-testid="skip-to-dashboard">
            J'installerai plus tard, accéder à mon espace <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
